import { useTranslation } from "react-i18next";
import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { cropAndCompressImage } from "@/lib/imageCompression";

interface ImageCropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageSrc: string;
  onCropComplete: (croppedImageBlob: Blob) => void;
  aspectRatio?: number | false;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const ImageCropDialog = ({

  open,
  onOpenChange,
  imageSrc,
  onCropComplete,
  aspectRatio = 1, // Square by default for avatars
  maxWidth = 400, // Default for avatars
  maxHeight = 400, // Default for avatars
  quality = 0.85, // Default for avatars
}: ImageCropDialogProps) => {
  const effectiveAspect = aspectRatio === false ? undefined : aspectRatio;
  const { t } = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);

  const onCropChange = useCallback((crop: { x: number; y: number }) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteInternal = useCallback(
    (_croppedArea: any, croppedAreaPixels: CropArea) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const createCroppedImage = async () => {
    if (!croppedAreaPixels) return;

    try {
      // Use the compression utility to crop and resize the image
      const croppedBlob = await cropAndCompressImage(
        imageSrc,
        croppedAreaPixels,
        maxWidth,
        maxHeight,
        quality
      );
      
      return croppedBlob;
    } catch (error) {
      console.error("Error creating cropped image:", error);
      throw error;
    }
  };

  const handleSave = async () => {
    try {
      const croppedBlob = await createCroppedImage();
      if (croppedBlob) {
        onCropComplete(croppedBlob);
      }
    } catch (error) {
      console.error("Failed to crop image:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('ui.text.crop_image')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="relative w-full h-[300px] sm:h-[400px] bg-black rounded-lg overflow-hidden">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={effectiveAspect}
              onCropChange={onCropChange}
              onZoomChange={onZoomChange}
              onCropComplete={onCropCompleteInternal}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('forms.labels.zoom')}</label>
            <Slider
              value={[zoom]}
              onValueChange={(value) => setZoom(value[0])}
              min={1}
              max={3}
              step={0.1}
              className="w-full"
            />
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button onClick={handleSave} className="w-full">
            {t('common.next')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
